import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useSearchParams, Link } from 'react-router-dom';
import DetailsModal from '@/components/DetailsModal';
import { ArrowUpRight, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

                                        
const PagesInsights = ({ websiteId, websiteDomain, period = '24h', startDate, endDate, token, analytics, channel, location, device, os, browser, activeGoalFilter, existingFilters = [] }) => {
        const [searchParams] = useSearchParams();

        // Auth header helper — token is null in publicMode; backend analytics endpoints
        // accept unauthenticated reads on public sites via get_current_user_optional.
        const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

        // Create stable filter string to prevent infinite re-renders
        const filtersKey = useMemo(() => existingFilters.join('|'), [existingFilters]);
        const [activeTab, setActiveTab] = useState('top');
        const [showModal, setShowModal] = useState(false);
        const [limit, setLimit] = useState(9); // Default to 9
        const [modalData, setModalData] = useState([]);
        const [modalLoading, setModalLoading] = useState(false);
        const [searchQuery, setSearchQuery] = useState('');
        const [sortConfig, setSortConfig] = useState({ key: 'pageviews', direction: 'desc' });
        
        // Pagination state for modal
        const [modalOffset, setModalOffset] = useState(0);
        const [hasMore, setHasMore] = useState(false);
        const [totalCount, setTotalCount] = useState(0);
        const [loadingMore, setLoadingMore] = useState(false);
        const MODAL_LIMIT = 100;

        // Build link URL for page filter
        const buildPageFilterUrl = (pagePath, filterType = 'page') => {
                const params = new URLSearchParams(searchParams);
                const filterParam = `is,${filterType},${pagePath}`;
                params.append('f', filterParam);
                return `/analytics/${websiteId}?${params.toString()}`;
        };

        // Get data based on active tab
        const getData = () => {

        console.log(analytics);
        console.log("-------fdsfsd sdf"); 
        if (!analytics) return [];

        if (activeTab === 'top') {
                return analytics.top_pages || [];
        } else if (activeTab === 'entry') {
                return analytics.entry_pages || [];
        } else if (activeTab === 'exit') {
                return analytics.exit_pages || [];
        }
        return [];
  };
  
  // Get total count based on active tab
  const getTotalCount = () => {
        if (!analytics) return 0;
        if (activeTab === 'top') {
                return analytics.total_pages || 0;
        } else if (activeTab === 'entry') {
                return analytics.total_entry_pages || 0;
        } else if (activeTab === 'exit') {
                return analytics.total_exit_pages || 0;
        }
        return 0;
  };

  const data = getData();
  
  // When goal filter is active, show only the goal's path
  let displayData;
  if (activeGoalFilter && activeGoalFilter.name) {
    // Extract path from goal name (e.g., "Visit /highlands/schedule" -> "/highlands/schedule")
    const goalPath = activeGoalFilter.name.replace(/^Visit\s+/i, '').trim();
    // Find matching page in data or create a placeholder
    const matchingPage = data.find(p => p.path === goalPath || p.path === goalPath + '/');
    displayData = matchingPage ? [matchingPage] : [{ path: goalPath, visitors: 0, pageviews: 0 }];
  } else {
    displayData = data.slice(0, limit);
  }
  
  const sectionTotalCount = getTotalCount();

  // Fetch modal data from API based on active tab
  useEffect(() => {
        const fetchModalData = async () => {
          // Public viewers have no token; backend allows unauthenticated reads
          // for is_public sites — only bail if there's no website to query.
          if (!showModal || !websiteId) return;

          setModalLoading(true);
          setModalOffset(0); // Reset offset when modal opens

          try {
                // Build URL with pagination
                let url = `${BACKEND_URL}/api/analytics/${websiteId}/details?type=pages&period=${period}&tab=${activeTab}&limit=${MODAL_LIMIT}&offset=0`;
                
                if (startDate && endDate) {
                  url += `&start_date=${startDate}&end_date=${endDate}`;
                }

                if (channel) url += `&channel=${encodeURIComponent(channel)}`;
                if (location) url += `&location=${encodeURIComponent(location)}`;
                if (device) url += `&device=${encodeURIComponent(device)}`;
                if (os) url += `&os=${encodeURIComponent(os)}`;
                if (browser) url += `&browser=${encodeURIComponent(browser)}`;
                
                // Add f filters
                if (existingFilters && existingFilters.length > 0) {
                        existingFilters.forEach(filter => {
                                url += `&f=${encodeURIComponent(filter)}`;
                        });
                }
                
                const response = await axios.get(url, { headers: authHeaders });
                
                const data = response.data;
                setModalData(data.records || []);
                setHasMore(data.has_more || false);
                setTotalCount(data.total || data.records?.length || 0);
                setModalOffset(MODAL_LIMIT);
          } catch (err) {
                console.error('Error fetching modal data:', err);
                setModalData([]);
                setHasMore(false);
          } finally {
                setModalLoading(false);
          }
        };

        fetchModalData();
  }, [showModal, activeTab, websiteId, period, startDate, endDate, token, filtersKey]);
  
  // Load more function for pagination
  const handleLoadMore = async () => {
        if (loadingMore || !hasMore) return;
        
        setLoadingMore(true);
        try {
                let url = `${BACKEND_URL}/api/analytics/${websiteId}/details?type=pages&period=${period}&tab=${activeTab}&limit=${MODAL_LIMIT}&offset=${modalOffset}`;
                
                if (startDate && endDate) {
                  url += `&start_date=${startDate}&end_date=${endDate}`;
                }
                if (channel) url += `&channel=${encodeURIComponent(channel)}`;
                if (location) url += `&location=${encodeURIComponent(location)}`;
                if (device) url += `&device=${encodeURIComponent(device)}`;
                if (os) url += `&os=${encodeURIComponent(os)}`;
                if (browser) url += `&browser=${encodeURIComponent(browser)}`;
                
                if (existingFilters && existingFilters.length > 0) {
                        existingFilters.forEach(filter => {
                                url += `&f=${encodeURIComponent(filter)}`;
                        });
                }
                
                const response = await axios.get(url, { headers: authHeaders });
                
                const data = response.data;
                setModalData(prev => [...prev, ...(data.records || [])]);
                setHasMore(data.has_more || false);
                setModalOffset(prev => prev + MODAL_LIMIT);
        } catch (err) {
                console.error('Error loading more:', err);
        } finally {
                setLoadingMore(false);
        }
  };

  // Filter and sort modal data
  const filteredModalData = modalData.filter(item => {
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        return item.page_url?.toLowerCase().includes(query);
  });

  const sortedModalData = [...filteredModalData].sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        }

        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();

        if (sortConfig.direction === 'asc') {
          return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
        } else {
          return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
        }
  });

  // Handle sorting
  const handleSort = (key) => {
        setSortConfig(prev => ({
          key,
          direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
        }));
  };

  return (
        <>
          <div className="data-table-container">
                <div className="table-header">
                  <h3 className="table-title">Top Pages</h3>
                  <div className="table-tabs">
                        <button
                          className={`table-tab ${activeTab === 'top' ? 'active' : ''}`}
                          onClick={() => setActiveTab('top')}
                          data-testid="tab-top-pages"
                        >
                          Top Pages
                        </button>
                        <button
                          className={`table-tab ${activeTab === 'entry' ? 'active' : ''}`}
                          onClick={() => setActiveTab('entry')}
                          data-testid="tab-entry-pages"
                        >
                          Entry Pages
                        </button>
                        <button
                          className={`table-tab ${activeTab === 'exit' ? 'active' : ''}`}
                          onClick={() => setActiveTab('exit')}
                          data-testid="tab-exit-pages"
                        >
                          Exit Pages
                        </button>
                  </div>
                </div>

                <div className="table-content">
                  {displayData.length === 0 && (
                        <div className="table-row">
                          <div className="table-cell page-cell">
                                <span className="no-data">No data available</span>
                          </div>
                        </div>
                  )}

                  {displayData.map((page, idx) => (
                        <div
                          key={idx}
                          className="table-row"
                          data-testid={`${activeTab}-page-${idx}`}
                          style={{ transition: 'background-color 0.2s' }}
                          onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f9fafb';
                          }}
                          onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <div className="table-cell page-cell" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Link
                                  to={buildPageFilterUrl(page.path, activeTab === 'entry' ? 'entry_page' : activeTab === 'exit' ? 'exit_page' : 'page')}
                                  className="page-path clickable"
                                  style={{ cursor: 'pointer', color: '#0f172a', textDecoration: 'none' }}
                                  title={page.path}
                                >
                                  {page.path}
                                </Link>
                                {websiteDomain && (
                                  <a
                                    href={`${websiteDomain.replace(/\/$/, '')}${page.path}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="external-link-icon"
                                    title={`Open ${page.path} on site`}
                                    onClick={(e) => e.stopPropagation()}
                                    data-testid={`external-link-${idx}`}
                                  >
                                    <ExternalLink size={14} />
                                  </a>
                                )}
                          </div>
                          <div className="table-cell number-cell">
                                <span>{page.visitors?.toLocaleString()}</span>
                                <ArrowUpRight className="trend-icon positive" />
                          </div>
                        </div>
                  ))}
                </div>
                
                {sectionTotalCount > limit && (
                  <button className="table-details" onClick={() => setShowModal(true)}>
                        ↻ DETAILS
                  </button>
                )}
          </div>

        {/* Details Modal */}
        <DetailsModal
                isOpen={showModal}
                websiteId={websiteId}
                websiteDomain={websiteDomain}
                device={device}
                period={period}
                startDate={startDate}
                endDate={endDate}
                os={os}
                browser={browser}
                location={location}
                channel={channel}
                onClose={() => {
                  setShowModal(false);
                  setSearchQuery('');
                  setSortConfig({ key: 'pageviews', direction: 'desc' });
                  setModalData([]);
                  setModalOffset(0);
                  setHasMore(false);
                }}
                title={`${activeTab === 'top' ? 'Top Pages' : activeTab === 'entry' ? 'Entry Pages' : 'Exit Pages'} - All Results`}
                type="pages"
                data={sortedModalData}
                loading={modalLoading}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                sortConfig={sortConfig}
                onSort={handleSort}
                activeTab={activeTab}
                hasMore={hasMore}
                onLoadMore={handleLoadMore}
                loadingMore={loadingMore}
                totalCount={totalCount}
          />
        </>
  );
};

export default PagesInsights;
